import {
  DestroyRef,
  Injectable,
  Signal,
  computed,
  inject,
  provideEnvironmentInitializer,
  signal,
} from '@angular/core';
import { createNoopInjectionToken } from 'ngxtension/create-injection-token';
import { injectCdnUrl } from '../common.config';
import {
  DEFAULT_COVER_FALLBACK_PATH,
  RuntimeAppConfig,
  loadRuntimeConfig,
} from '../runtime-config';

export const [, , RUNTIME_CONFIG] =
  createNoopInjectionToken<RuntimeAppConfig>('Runtime App Config');

const [, , RUNTIME_CDN_URL] = createNoopInjectionToken<string>(
  'Runtime Config CDN URL',
);

@Injectable()
export class RuntimeConfigService {
  readonly #cdnUrl = inject(RUNTIME_CDN_URL);
  readonly #config = signal<RuntimeAppConfig>(inject(RUNTIME_CONFIG), {
    equal: (a, b) => JSON.stringify(a) === JSON.stringify(b),
  });
  readonly config = this.#config.asReadonly();

  constructor() {
    const destroyRef = inject(DestroyRef);
    let timerId: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      timerId = setTimeout(async () => {
        try {
          const updated = await loadRuntimeConfig(this.#cdnUrl);
          this.#config.set(updated);
        } catch {
          // fetch failed — keep the current config and reschedule normally
        } finally {
          schedule();
        }
      }, this.#config().ttl);
    };

    schedule();
    destroyRef.onDestroy(() => clearTimeout(timerId));
  }
}

/**
 * Absolute URL of the cover image to render for events that have none of their
 * own. Any surface showing an event cover can use this instead of hiding the
 * image, so a missing cover never leaves a hole in a layout.
 *
 * Tracks `coverFallback` from the runtime config, so the artwork can be
 * swapped by republishing config.json — no redeploy. RuntimeConfigService is
 * optional: apps that don't provide it (control-hub) still get the default.
 */
export function injectCoverFallbackUrl(): Signal<string> {
  const cdnUrl = injectCdnUrl();
  const runtimeConfig = inject(RuntimeConfigService, { optional: true });

  return computed(
    () =>
      `${cdnUrl}${runtimeConfig?.config().coverFallback ?? DEFAULT_COVER_FALLBACK_PATH}`,
  );
}

export function provideRuntimeConfigService(
  cdnUrl: string,
  initialConfig: RuntimeAppConfig,
) {
  return [
    { provide: RUNTIME_CONFIG, useValue: initialConfig },
    { provide: RUNTIME_CDN_URL, useValue: cdnUrl },
    RuntimeConfigService,
    provideEnvironmentInitializer(() => inject(RuntimeConfigService)),
  ];
}
